import React, { useCallback, useEffect, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import './UsersView.css';

const ROLE_OPTIONS = [
  { value: 'admin',          label: 'Admin' },
  { value: 'manager',        label: 'Manager' },
  { value: 'service_writer', label: 'Service Writer' },
  { value: 'technician',     label: 'Technician' },
  { value: 'dispatcher',     label: 'Dispatcher' },
  { value: 'parts',          label: 'Parts' },
  { value: 'accounting',     label: 'Accounting' },
];

const STATUS_LABELS = {
  CONFIRMED: 'Active',
  FORCE_CHANGE_PASSWORD: 'Invite sent',
  RESET_REQUIRED: 'Reset required',
  UNCONFIRMED: 'Unconfirmed',
};

async function callUsersApi(body) {
  const { tokens } = await fetchAuthSession();
  const token = tokens?.accessToken?.toString();
  if (!token) throw new Error('Not signed in');
  const res = await fetch('/api/users', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

export default function UsersView() {
  const [users, setUsers] = useState([]);
  const [selfId, setSelfId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null); // username currently being mutated
  const [notice, setNotice] = useState('');

  const [form, setForm] = useState({ name: '', email: '', role: 'technician' });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await callUsersApi({ action: 'list' });
      setUsers(data.users || []);
      setSelfId(data.self || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function flash(msg) {
    setNotice(msg);
    setTimeout(() => setNotice(''), 4000);
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.email.trim()) return;
    setCreating(true);
    setError('');
    try {
      await callUsersApi({ action: 'create', ...form });
      setForm({ name: '', email: '', role: 'technician' });
      flash(`Invite emailed to ${form.email.trim()} with a temporary password.`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  }

  async function mutate(username, body, successMsg) {
    setBusy(username);
    setError('');
    try {
      await callUsersApi({ username, ...body });
      if (successMsg) flash(successMsg);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="uv-root">
      <div className="uv-header">
        <div>
          <h1>Users</h1>
          <p>Staff accounts for this shop. New users get an email with a temporary password.</p>
        </div>
      </div>

      {error && <div className="uv-banner uv-banner--error">{error}</div>}
      {notice && <div className="uv-banner uv-banner--ok">{notice}</div>}

      <form className="uv-create" onSubmit={handleCreate}>
        <input
          type="text"
          placeholder="Full name"
          value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
        />
        <input
          type="email"
          placeholder="email@example.com"
          required
          value={form.email}
          onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
        />
        <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
          {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <button type="submit" disabled={creating}>{creating ? 'Creating…' : 'Add User'}</button>
      </form>

      <div className="uv-table-card">
        {loading ? (
          <div className="uv-empty">Loading users…</div>
        ) : error ? (
          <div className="uv-empty">The user list could not load. Fix the error above, then <button className="uv-retry" onClick={load}>retry</button>.</div>
        ) : users.length === 0 ? (
          <div className="uv-empty">No users yet.</div>
        ) : (
          <table className="uv-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const isSelf = u.username === selfId;
                const isBusy = busy === u.username;
                return (
                  <tr key={u.username} className={u.enabled ? '' : 'uv-row--disabled'}>
                    <td>
                      <div className="uv-name">{u.name || '—'}{isSelf && <span className="uv-self-tag">you</span>}</div>
                      <div className="uv-email">{u.email}</div>
                    </td>
                    <td>
                      <select
                        value={u.role || ''}
                        disabled={isBusy}
                        onChange={e => mutate(u.username, { action: 'set-role', role: e.target.value }, 'Role updated.')}
                      >
                        {!u.role && <option value="">No role</option>}
                        {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </td>
                    <td>
                      <span className={`uv-status uv-status--${u.enabled ? (u.status === 'CONFIRMED' ? 'active' : 'pending') : 'off'}`}>
                        {u.enabled ? (STATUS_LABELS[u.status] || u.status) : 'Disabled'}
                      </span>
                    </td>
                    <td className="uv-actions">
                      <button
                        disabled={isBusy}
                        onClick={() => mutate(u.username, { action: 'reset-password' }, 'Password reset email sent.')}
                      >
                        Reset password
                      </button>
                      <button
                        disabled={isBusy || isSelf}
                        className={u.enabled ? 'uv-danger' : ''}
                        title={isSelf ? 'You cannot disable your own account' : undefined}
                        onClick={() => mutate(u.username, { action: u.enabled ? 'disable' : 'enable' }, u.enabled ? 'User disabled.' : 'User enabled.')}
                      >
                        {u.enabled ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
